// src/components/MenuBar.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import LedgerPopup from "./LedgerPopup";
import VoucherEntryPopup from "./VoucherEntryPopup";
import ReactDOM from "react-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { db } from "../firebase";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { hasPermission } from "../config/permissions";
import { canAccessSalesDataImport } from "../features/salesDataImport/access";
import { canAccessBi } from "../features/bi/access";
import { useLiveBuildVersionLabel } from "../hooks/useLiveBuildVersionLabel";
import PageSerialBadge from "./PageSerialBadge";
import WinsAutosLogo from "./WinsAutosLogo";
import {
  VOUCHER_TRANSACTION_PAGES,
  voucherPagePermissions,
} from "../config/voucherTransactionPages";
import { useFinancialYear } from "../context/FinancialYearContext";

const HEADER_SPACER_HEIGHT = "76px";

export default function MenuBar() {
  const version = useLiveBuildVersionLabel();
  const [showLedgerPopup, setShowLedgerPopup] = useState(false);
  const [showVoucherPopup, setShowVoucherPopup] = useState(false);
  const [pendingOrdersBadge, setPendingOrdersBadge] = useState(0);
  const [openMenu, setOpenMenu] = useState(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [submenuTop, setSubmenuTop] = useState(0);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  let user = {};
  try {
    user = JSON.parse(localStorage.getItem("user") || "{}") || {};
  } catch {
    user = {};
  }
  const role = user.role || "viewer";
  const companyName = "WINS AUTOS";
  const { fyStartYear, setFyStartYear, periodRange, options: fyOptions } = useFinancialYear();
  const permissionsSerialized = JSON.stringify(user.permissions ?? null);

  const menus = useMemo(() => {
    const u = { role, permissions: JSON.parse(permissionsSerialized) };
    const can = (id) => hasPermission(u, id);
    const file = [];
    if (can("dashboard")) file.push("Dashboard");
    if (can("party_creation")) file.push("Party Creation");
    if (can("admin")) file.push("Admin Panel");
    if (can("admin")) file.push("Firestore Studio");
    if (can("admin")) file.push("Firestore Manager");
    if (can("change_password")) file.push("Change my password");
    file.push("Exit");

    const masters =
      can("masters")
        ? [
            "Products",
            "Ledger Account",
            "Group",
            "Party discount / commission",
            "Brand",
            "Unit",
            "GST",
            "HSN",
            "partyDiscounts",
            "Party discount amounts",
            "Credit / Due (data)",
          ]
        : [];

    const txItems = [
      ["Trading", "transactions"],
      ["Credit / Due", "credit_due"],
      ["Purchase Orders", "purchase_orders"],
      ["Pending orders", "pending_orders"],
      ["Voucher Entry", "transactions"],
      ["View Stock", "transactions"],
    ];
    const transactions = [];
    if (can("billing")) transactions.push("Outward Supply");
    if (can("billing") || can("purchase")) transactions.push("Inward Supply");
    transactions.push(...txItems.filter(([, perm]) => can(perm)).map(([label]) => label));

    const payroll = can("payroll")
      ? [
          "Monitor",
          "Monthly report",
          "Daily hours",
          "Holidays",
          "Daily late report",
          "Yearly attendance",
          "Employees",
          "Payroll reports",
          "Punch photos",
          "Employee directory (CSV)",
          "Joiners / leavers / anniversary",
          "Monthly Status Report (Detailed Work Duration(Four Punch))",
          "Weekly & fortnightly attendance",
          "OT summary (month)",
          "Shift adherence",
          "Branch comparison",
          "Attendance audit",
        ]
      : [];

    const tools = can("tools") ? [
      "Add Item",
      "Excel Import", 
      "Pending order email",
      "Expense upload",
      "Sales Data Import",
    ] : [];

    const cctvItems = [
      ["CCTV live", "cctv"],
      ["CCTV cameras", "cctv_admin"],
      ["CCTV settings", "cctv_admin"],
    ];
    const cctv = cctvItems.filter(([, perm]) => can(perm)).map(([label]) => label);

    const accounting = can("accounting") ? ["Ledger Account View", "AccountBooks"] : [];

    const reportItems = [
      ["Stock Report", "reports"],
      ["Movement Analysis", "reports"],
      ["Stock Forecast", "reports"],
      ["Monthly sale report (import)", "monthly_sale_report"],
      ["Monthly import — generate totals", "monthly_sale_report"],
      ["Expense party report", "monthly_sale_report"],
      ["Employee group monthly", "monthly_sale_report"],
      ["Expense transfer report", "monthly_sale_report"],
      ["Expense Spare Way report", "monthly_sale_report"],
      ["Monthly incentive", "monthly_incentive"],
      ["Monthly incentive — saved", "monthly_incentive"],
      ["Yearly sale report", "yearly_sale_report"],
      ["Customer entry", "customer_entry"],
      ["Order reports", "order_reports"],
      ["Ledger Account View", "accounting"],
      ["Sales Ageing", "sales_ageing"],
      ["PROFIT AND LOSS ACCOUNT", "profit_loss"],
      ["AccountBooks", "accounting"],
    ];
    let reports = reportItems
      .filter(([label, perm]) => {
        if (label === "Movement Analysis") {
          return can("reports") || can("transactions") || can("movement_analysis");
        }
        return can(perm);
      })
      .map(([label]) => label);
    if (canAccessSalesDataImport(u)) {
      reports = [
        ...reports,
        "Sales import history",
        "Sales import records",
        "Sales advanced reports",
        "Sales voucher list",
        "Branch monthly sales & purchase",
        "Brand by branch",
        "Group by branch",
        "Discount vs non-discount sales",
        "Daily items on sale",
        "Branch voucher reports",
      ];
    }

    const bi = canAccessBi(u)
      ? [
          "BI Dashboard",
          "BI Business Health Score",
          "BI Executive Dashboard",
          "BI Slow Moving",
          "BI Fast Moving",
          "BI Stock Forecast",
          "BI Dead Stock",
          "BI Target vs Achievement",
          "BI Brand Growth",
          "BI Employee Performance",
          "BI Profit Analysis",
          "BI AI Insights",
        ]
      : [];

    // Personal: own data upload / download / edit (also show if user has change_password)
    const personal = [];
    if (can("personal") || can("change_password")) {
      personal.push("My personal data");
    }
    if (can("change_password")) personal.push("Change my password");

    return {
      File: file,
      Personal: personal,
      Masters: masters,
      Transactions: transactions,
      Payroll: payroll,
      "Tools & Utilities": tools,
      CCTV: cctv,
      Accounting: accounting,
      Reports: reports,
      "Business Intelligence": bi,
    };
  }, [role, permissionsSerialized]);

  const submenus = useMemo(() => {
    const u = { role, permissions: JSON.parse(permissionsSerialized) };
    const can = (id) => hasPermission(u, id);
    const tradingItems = VOUCHER_TRANSACTION_PAGES.filter((page) =>
      voucherPagePermissions(page).some((perm) => can(perm))
    ).map((p) => p.label);

    return {
      AccountBooks: ["CashBook", "BankBook"],
      Trading: tradingItems,
      "GST Tools": ["GSTReturn", "E-Invoice"],
    };
  }, [role, permissionsSerialized]);

  useEffect(() => {
    const computePending = (po) => {
      const status = String(po?.status ?? "").trim().toLowerCase();
      if (status.includes("cancel")) return false;
      if (status.includes("reject")) return false;
      if (status.includes("approve")) return false;
      if (status.includes("received")) return false;
      if (po?.orderReceived === true || po?.received === true) return false;
      return true;
    };

    const qy = query(collection(db, "orders"), orderBy("createdAt", "desc"), limit(500));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        let c = 0;
        snap.docs.forEach((d) => {
          const data = d.data();
          if (computePending(data)) c += 1;
        });
        setPendingOrdersBadge(c);
      },
      () => {}
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        closeMenus();
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") closeMenus();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  const closeMenus = () => {
    setOpenMenu(null);
    setActiveSubmenu(null);
    setSubmenuTop(0);
  };

  const showSubmenu = (item, e) => {
    const panel = e.currentTarget.closest("[data-menu-panel]");
    if (panel) {
      const panelTop = panel.getBoundingClientRect().top;
      const itemTop = e.currentTarget.getBoundingClientRect().top;
      setSubmenuTop(Math.max(0, itemTop - panelTop - 6));
    }
    setActiveSubmenu(item);
  };

  const getSubRoute = (sub) => {
    if (sub === "PDF") return "/pdfExport";
    if (sub === "Excel") return "/excel-import";
    const tradingPage = VOUCHER_TRANSACTION_PAGES.find((p) => p.label === sub);
    if (tradingPage) return tradingPage.path;
    if (sub === "GSTReturn") return "/gstr1";
    if (sub === "SGST") return "/sgstReport";
    if (sub === "CashBook") return "/cash-book";
    if (sub === "BankBook") return "/bank-book";
    return "#";
  };

  const getRouteForItem = (item) => {
    switch (item) {
      case "Dashboard": return "/dashboard";
      case "Outward Supply":
      case "Billing":
        return "/billing";
      case "Credit / Due": return "/credit-due";
      case "Purchase":
      case "Inward Supply":
        return "/voucher/inward-supply";
      case "Reports": return "/reports";
      case "Party Creation": return "/party-creation";
      case "Admin Panel": return "/admin";
      case "Firestore Studio": return "/admin/projects";
      case "Firestore Manager": return "/admin/firestore-manager";
      case "Change my password": return "/change-password";
      case "My personal data": return "/personal";
      case "Products": return "/add-product";
      case "Ledger Account": return "/party-creation";
      case "View Stock": return "/monthly-product-report";
      case "Sales Report": return "/reports";
      case "Stock Report": return "/stock-report";
      case "Sales Data Import": return "/sales-data-import";
      case "Sales import history": return "/sales-data-import/history";
      case "Sales import records": return "/sales-data-import/records";
      case "Sales advanced reports": return "/sales-data-import/advanced-reports";
      case "Sales voucher list": return "/sales-data-import/voucher-list";
      case "Branch monthly sales & purchase": return "/sales-data-import/branch-monthly";
      case "Brand by branch": return "/sales-data-import/brand-by-branch";
      case "Group by branch": return "/sales-data-import/group-by-branch";
      case "Discount vs non-discount sales": return "/sales-data-import/discount-sales";
      case "Daily items on sale": return "/sales-data-import/items-on-sale";
      case "Branch voucher reports": return "/sales-data-import/branch-voucher-reports";
      case "Monthly sale report (import)": return "/reports/monthly-sale-report-import";
      case "Monthly import — generate totals": return "/reports/monthly-sale-report-import/generate";
      case "Expense upload": return "/reports/expense-upload";
      case "Expense party report": return "/reports/expense-party-monthly";
      case "Party brand monthly":
      case "Employee brand monthly":
      case "Employee group monthly":
        return "/reports/party-brand-monthly";
      case "Expense transfer report": return "/reports/expense-transfer-monthly";
      case "Expense Spare Way report": return "/reports/expense-spareway-monthly";
      case "Monthly incentive": return "/reports/monthly-incentive";
      case "Monthly incentive — saved": return "/reports/monthly-incentive/view";
      case "Yearly sale report": return "/reports/yearly-sale-report";
      case "Movement Analysis": return "/movement-analysis";
      case "Stock Forecast": return "/stock-forecast";
      case "Sales Ageing": return "/party-ageing-report";
      case "Group": return "/group-master";
      case "Party discount / commission":
      case "Group discount / commission":
        return "/group-commission-settings";
      case "Brand": return "/brand-master";
      case "Unit": return "/unit-master";
      case "GST": return "/gst-master";
      case "HSN": return "/hsn-master";
      case "partyDiscounts": return "/party-Discounts";
      case "Party discount amounts": return "/party-discount-amounts";
      case "Credit / Due (data)": return "/credit-due-master";
      case "PROFIT AND LOSS ACCOUNT": return "/profit-loss";
      case "Purchase Orders": return "/purchase-orders";
      case "Pending orders": return "/pending-orders";
      case "Customer entry": return "/reports/customer-entry";
      case "Order reports": return "/reports/order-reports";
      case "Monitor": return "/payroll";
      case "Monthly report": return "/payroll/monthly-report";
      case "Daily hours": return "/payroll/daily-hours";
      case "Holidays": return "/payroll/holidays";
      case "Daily late report": return "/payroll/daily-late";
      case "Yearly attendance": return "/payroll/yearly-attendance";
      case "Employees": return "/payroll/employees";
      case "Payroll reports": return "/payroll/reports";
      case "Punch photos": return "/payroll/attendance-images";
      case "Employee directory (CSV)": return "/payroll/reports/employee-directory";
      case "Joiners / leavers / anniversary": return "/payroll/reports/tenure";
      case "Monthly Status Report (Detailed Work Duration(Four Punch))": return "/payroll/reports/employee-attendance-card";
      case "Weekly & fortnightly attendance": return "/payroll/reports/period-attendance";
      case "OT summary (month)": return "/payroll/reports/ot-summary";
      case "Shift adherence": return "/payroll/reports/shift-adherence";
      case "Branch comparison": return "/payroll/reports/branch-comparison";
      case "Attendance audit": return "/payroll/reports/attendance-audit";
      case "Excel Import": return "/excel-import";
      case "Pending order email": return "/tools/pending-order-email";
      case "CCTV live": return "/cctv";
      case "CCTV cameras": return "/cctv/cameras";
      case "CCTV settings": return "/tools/cctv-settings";
      case "BI Dashboard": return "/bi";
      case "BI Business Health Score": return "/bi/business-health-score";
      case "BI Executive Dashboard": return "/bi/executive";
      case "BI Slow Moving": return "/bi/slow-moving";
      case "BI Fast Moving": return "/bi/fast-moving";
      case "BI Stock Forecast": return "/bi/forecast";
      case "BI Dead Stock": return "/bi/dead-stock";
      case "BI Target vs Achievement": return "/bi/target";
      case "BI Brand Growth": return "/bi/brand-growth";
      case "BI Employee Performance": return "/bi/employee";
      case "BI Profit Analysis": return "/bi/profit";
      case "BI AI Insights": return "/bi/insights";
      default: {
        const tradingPage = VOUCHER_TRANSACTION_PAGES.find((p) => p.label === item);
        if (tradingPage) return tradingPage.path;
        return "#";
      }
    }
  };

  const renderSubItem = (sub, j) => {
    const toPath = getSubRoute(sub);
    return (
      <NavLink
        key={j}
        to={toPath}
        className={({ isActive }) => `erp-dropdown-item ${isActive ? "erp-dropdown-item-active" : ""}`}
        onClick={closeMenus}
      >
        {sub}
      </NavLink>
    );
  };

  const clearSubmenu = () => {
    setActiveSubmenu(null);
    setSubmenuTop(0);
  };

  const renderMenuItem = (item, i) => {
    if (item === "Voucher Entry") {
      return (
        <button
          key={i}
          type="button"
          className="erp-dropdown-item w-full text-left"
          onMouseEnter={clearSubmenu}
          onClick={() => {
            closeMenus();
            setShowVoucherPopup(true);
          }}
        >
          {item}
        </button>
      );
    }

    if (item === "Ledger Account View") {
      return (
        <button
          key={i}
          type="button"
          className="erp-dropdown-item w-full text-left"
          onMouseEnter={clearSubmenu}
          onClick={() => {
            closeMenus();
            setShowLedgerPopup(true);
          }}
        >
          {item}
        </button>
      );
    }

    if (item === "Exit") {
      return (
        <button
          key={i}
          type="button"
          className="erp-dropdown-item w-full text-left"
          onMouseEnter={clearSubmenu}
          onClick={async () => {
            try {
              await signOut(auth);
            } catch (e) {
              console.warn("signOut failed", e);
            }
            localStorage.removeItem("user");
            closeMenus();
            navigate("/login");
          }}
        >
          {item}
        </button>
      );
    }

    const toPath = getRouteForItem(item);

    return (
      <NavLink
        key={i}
        to={toPath}
        end={toPath === "/payroll"}
        className={({ isActive }) => `erp-dropdown-item ${isActive ? "erp-dropdown-item-active" : ""}`}
        onMouseEnter={clearSubmenu}
        onClick={closeMenus}
      >
        <span className="flex flex-1 items-center justify-between gap-3">
          <span>{item}</span>
          {item === "Pending orders" && pendingOrdersBadge > 0 && (
            <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-rose-600 px-2 py-0.5 text-[11px] font-bold text-white">
              {pendingOrdersBadge > 99 ? "99+" : pendingOrdersBadge}
            </span>
          )}
        </span>
      </NavLink>
    );
  };

  const renderSubmenuTrigger = (item, i) => (
    <div
      key={i}
      role="menuitem"
      className={`erp-dropdown-item erp-dropdown-item-header cursor-default ${
        activeSubmenu === item ? "bg-[rgba(93,0,129,0.04)] text-erp-brand" : ""
      }`}
      onMouseEnter={(e) => showSubmenu(item, e)}
    >
      <span>{item}</span>
      <span className="material-symbols-outlined text-[16px] opacity-40">chevron_right</span>
    </div>
  );

  const visibleMenus = Object.keys(menus).filter(
    (menuKey) => menus[menuKey] && menus[menuKey].length > 0
  );

  return (
    <>
      <div style={{ height: HEADER_SPACER_HEIGHT }} aria-hidden />

      <header
        ref={menuRef}
        className="fixed top-0 left-0 right-0 z-[1000] w-full overflow-visible border-b border-outline-variant/40 bg-white"
      >
        <nav className="flex h-10 items-center justify-between overflow-visible px-6" aria-label="Top navigation">
          <div className="flex h-full items-center gap-6 overflow-visible">
            {visibleMenus.map((menuKey) => {
              const items = menus[menuKey];
              const isOpen = openMenu === menuKey;

              return (
                <div
                  key={menuKey}
                  className="relative flex h-full items-center"
                  onMouseEnter={() => setOpenMenu(menuKey)}
                  onMouseLeave={() => {
                    setOpenMenu(null);
                    clearSubmenu();
                  }}
                >
                  <span
                    className={`erp-menu-item h-full border-b-2 border-transparent ${
                      isOpen ? "border-erp-brand text-erp-brand" : ""
                    }`}
                  >
                    {menuKey}
                    <span className="material-symbols-outlined text-[14px]">expand_more</span>
                  </span>

                  {isOpen && (
                    <div className="absolute left-0 top-full z-50 pt-0.5">
                      <div className="relative min-w-[14rem]" data-menu-panel>
                        <div
                          className="erp-dropdown-animate max-h-[65vh] w-56 overflow-y-auto rounded-lg py-1.5 erp-dropdown-glass"
                          onScroll={clearSubmenu}
                        >
                          {items.map((item, i) =>
                            submenus[item] ? renderSubmenuTrigger(item, i) : renderMenuItem(item, i)
                          )}
                        </div>

                        {activeSubmenu && submenus[activeSubmenu] && (
                          <div
                            className="absolute left-full top-0 z-[60] -ml-1 pl-2"
                            style={{ top: submenuTop }}
                            onMouseEnter={() => setActiveSubmenu(activeSubmenu)}
                          >
                            <div className="erp-dropdown-animate w-52 max-h-[65vh] overflow-y-auto rounded-lg py-1.5 erp-dropdown-glass">
                              {submenus[activeSubmenu].map((sub, j) => renderSubItem(sub, j))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {user && (
            <button
              type="button"
              className="rounded-full bg-zinc-900 px-4 py-1.5 text-[10px] font-bold tracking-widest text-white shadow-md transition-all hover:bg-black active:scale-95"
              aria-label={`Signed in as ${user.role || "guest"}`}
            >
              {String(user.role || "GUEST").toUpperCase()}
            </button>
          )}
        </nav>

        <div
          className="flex items-center justify-between border-t border-white/20 px-3 py-1 text-white"
          style={{ background: "linear-gradient(90deg, #4b006f, #7200c6)" }}
        >
          <div className="flex items-center gap-2">
            <WinsAutosLogo size={28} className="flex-shrink-0 ring-1 ring-white/20" />
            <PageSerialBadge />
            <div className="text-[11px] font-semibold leading-tight">{version}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] font-semibold leading-tight">{companyName}</div>
            <label className="mt-0.5 flex items-center justify-end gap-1.5 text-[10px] leading-tight">
              <span className="opacity-90">FY</span>
              <select
                value={fyStartYear}
                onChange={(e) => setFyStartYear(Number(e.target.value))}
                className="max-w-[5.5rem] cursor-pointer rounded border border-white/35 bg-white/15 px-1 py-0 text-[10px] font-semibold text-white outline-none hover:bg-white/25 focus:border-white/60"
                title="Financial year (April–March)"
                aria-label="Financial year"
              >
                {fyOptions.map(({ startYear, label }) => (
                  <option key={startYear} value={startYear} className="text-zinc-900">
                    {label}
                  </option>
                ))}
              </select>
              <span className="hidden opacity-90 sm:inline">{periodRange}</span>
            </label>
          </div>
        </div>

        {showLedgerPopup &&
          ReactDOM.createPortal(
            <LedgerPopup onClose={() => setShowLedgerPopup(false)} />,
            document.body
          )}
        {showVoucherPopup &&
          ReactDOM.createPortal(
            <VoucherEntryPopup onClose={() => setShowVoucherPopup(false)} />,
            document.body
          )}
      </header>
    </>
  );
}
